/**
 * VersionSidebar - Vertical navigation for document versions
 *
 * Displays a list of version summaries in a sidebar, allowing users
 * to click and view different versions. Supports compare mode for
 * selecting two versions to diff.
 */
import { format } from "date-fns";
import { History, Clock, ChevronRight, GitCompare, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import type { DocumentVersionSummary } from "../../../types/version";

interface VersionSidebarProps {
  /** List of version summaries to display */
  versions: DocumentVersionSummary[];
  /** Currently selected version number (null = current document) */
  selectedVersion: number | null;
  /** Callback when a version is selected */
  onSelectVersion: (versionNumber: number) => void;
  /** Whether versions are still loading */
  isLoading?: boolean;
  /** Current document version count (for "Current" badge) */
  currentVersionCount?: number;
  /** Whether compare mode is active */
  compareMode?: boolean;
  /** Toggle compare mode */
  onToggleCompareMode?: () => void;
  /** Versions selected for comparison */
  compareVersions?: number[];
  /** Toggle a version for comparison */
  onToggleCompareVersion?: (versionNumber: number) => void;
}

export function VersionSidebar({
  versions,
  selectedVersion,
  onSelectVersion,
  isLoading = false,
  currentVersionCount = 1,
  compareMode = false,
  onToggleCompareMode,
  compareVersions = [],
  onToggleCompareVersion,
}: VersionSidebarProps) {
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="w-72 border-r border-gray-200 bg-gray-50 p-4">
        <div className="mb-4 flex items-center gap-2">
          <History className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-700">Version History</h2>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse rounded-lg bg-gray-200 p-3 h-16"
            />
          ))}
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="w-72 border-r border-gray-200 bg-gray-50 p-4">
        <div className="mb-4 flex items-center gap-2">
          <History className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-700">Version History</h2>
        </div>
        <div className="rounded-lg bg-white p-4 text-center text-gray-500 shadow-sm">
          <p className="text-sm">No previous versions</p>
          <p className="mt-1 text-xs text-gray-400">
            Versions are created when the document is edited
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-72 border-r border-gray-200 bg-gray-50 flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-700">Version History</h2>
          </div>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {versions.length} previous version{versions.length !== 1 ? "s" : ""}
        </p>

        {/* Compare mode toggle */}
        {versions.length >= 2 && onToggleCompareMode && (
          <button
            onClick={onToggleCompareMode}
            className={cn(
              "mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
              compareMode
                ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            )}
          >
            {compareMode ? (
              <>
                <X className="h-4 w-4" />
                Exit Compare
              </>
            ) : (
              <>
                <GitCompare className="h-4 w-4" />
                Compare Versions
              </>
            )}
          </button>
        )}

        {/* Compare mode instructions */}
        {compareMode && (
          <p className="mt-2 text-xs text-indigo-600 text-center">
            Select 2 versions to compare
            {compareVersions.length > 0 && ` (${compareVersions.length}/2 selected)`}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <div className="space-y-2">
          {versions.map((version) => {
            const isSelected = selectedVersion === version.version_number;
            const isMostRecent = version.version_number === Math.max(...versions.map(v => v.version_number));
            const isSelectedForCompare = compareVersions.includes(version.version_number);
            const canSelectForCompare = compareVersions.length < 2 || isSelectedForCompare;

            return (
              <div
                key={version.id}
                className={cn(
                  "w-full rounded-lg transition-all duration-150",
                  compareMode
                    ? isSelectedForCompare
                      ? "ring-2 ring-indigo-500 bg-indigo-50"
                      : "bg-gray-100 hover:bg-white hover:shadow-sm"
                    : isSelected
                    ? "bg-white shadow-md ring-2 ring-indigo-500"
                    : "bg-gray-100 hover:bg-white hover:shadow-sm"
                )}
              >
                <button
                  onClick={() => {
                    if (compareMode && onToggleCompareVersion) {
                      if (canSelectForCompare) {
                        onToggleCompareVersion(version.version_number);
                      }
                    } else {
                      onSelectVersion(version.version_number);
                    }
                  }}
                  disabled={compareMode && !canSelectForCompare}
                  className={cn(
                    "w-full text-left p-3",
                    compareMode && !canSelectForCompare && "opacity-50 cursor-not-allowed"
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {/* Checkbox for compare mode */}
                        {compareMode && (
                          <div
                            className={cn(
                              "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                              isSelectedForCompare
                                ? "bg-indigo-600 border-indigo-600"
                                : "border-gray-300 bg-white"
                            )}
                          >
                            {isSelectedForCompare && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </div>
                        )}
                        <span className={cn(
                          "text-sm font-medium",
                          compareMode && isSelectedForCompare
                            ? "text-indigo-700"
                            : isSelected && !compareMode
                            ? "text-indigo-700"
                            : "text-gray-700"
                        )}>
                          Version {version.version_number}
                        </span>
                        {isMostRecent && (
                          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                            Latest
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 truncate" title={version.title}>
                        {version.title}
                      </p>
                      <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(version.created_at)}</span>
                      </div>
                    </div>
                    {!compareMode && isSelected && (
                      <ChevronRight className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
